import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useMarkCommunityJoinedMutation,
  useUpdateOnboardingMutation,
} from '@/generated/graphql';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { buildBrandsLandingUrl } from '@/lib/brandLeads/brandsLanding';
import type { HireClippersProfile } from '@/lib/brandLeads/hireClippersDraft';
import {
  dismissCommunityPrompt,
  WHATSAPP_COMMUNITY_URL,
} from '@/lib/community/communityPrompt';
import {
  buildOnboardingCompletedProperties,
  type OnboardingCourseOfferDecision,
} from '@/lib/onboarding/buildOnboardingCompletedProperties';
import { resolveExperienceOffer } from '@/lib/onboarding/experienceOffer';
import { useRecordOnboardingCourseOffer } from '@/lib/onboarding/useRecordOnboardingCourseOffer';
import {
  ONBOARDING_EXPERIENCES,
  ONBOARDING_OBJECTIVE_CHOICES,
  type OnboardingExperience,
  type OnboardingObjectiveChoice,
  resolveObjectiveSelection,
} from '@/lib/onboarding/onboardingSelections';
import {
  ClientWorkIllustration,
  ClippingChannelIllustration,
  OwnContentIllustration,
} from '@/components/onboarding/OnboardingIllustrations';
import { OnboardingOptionCard } from '@/components/onboarding/OnboardingOptionCard';
import { OnboardingCourseOffer } from '@/components/onboarding/OnboardingCourseOffer';
import { OnboardingCommunityOffer } from '@/components/onboarding/OnboardingCommunityOffer';

interface OnboardingModalProps {
  open: boolean;
  // Returning false synchronously rejects continuations for a stale session.
  onComplete: () => boolean | void | Promise<unknown>;
  onRequestViralizeCourse: (
    trigger?: HTMLElement | null,
    options?: { replayQuestionnaire?: boolean },
  ) => Promise<void>;
  previewMode?: boolean;
  /** Pre-fills the brands page the hire-clippers card opens. */
  profile?: HireClippersProfile | null;
}

type OnboardingStep =
  | 'objective'
  | 'experience'
  | 'courseOffer'
  | 'communityOffer';

type OnboardingCommunityDecision = 'joined' | 'dismissed';

const buildSelectionAnalyticsProperties = (
  choice: OnboardingObjectiveChoice,
) => {
  const selection = resolveObjectiveSelection(choice);
  return {
    objective: selection.onboardingObjective,
    jobTitle: selection.jobTitle,
    persona: choice,
  };
};

const safeTrackEvent = (
  event: string,
  properties: Record<string, string | number | boolean | undefined> = {},
) => {
  try {
    trackEvent(event, properties);
  } catch (error) {
    console.warn(`Unable to track ${event}`, error);
  }
};

export const OnboardingModal = ({
  open,
  onComplete,
  onRequestViralizeCourse,
  previewMode = false,
  profile = null,
}: OnboardingModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [updateOnboarding] = useUpdateOnboardingMutation();
  const [markCommunityJoined] = useMarkCommunityJoinedMutation();
  const [recordOnboardingCourseOffer] = useRecordOnboardingCourseOffer();
  const [step, setStep] = useState<OnboardingStep>('objective');
  const [objectiveChoice, setObjectiveChoice] =
    useState<OnboardingObjectiveChoice | null>(null);
  const [experience, setExperience] =
    useState<OnboardingExperience | null>(null);
  const [pendingCourseOfferDecision, setPendingCourseOfferDecision] =
    useState<OnboardingCourseOfferDecision | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isSubmittingRef = useRef(false);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);
  const persistedRef = useRef(false);
  const onboardingStartedRef = useRef(false);
  const courseOfferViewedRef = useRef(false);
  const communityOfferViewedRef = useRef(false);
  const brandsLandingOpenedRef = useRef(false);
  const trackedOfferDecisionsRef = useRef(
    new Set<OnboardingCourseOfferDecision>(),
  );
  const trackedCommunityDecisionsRef = useRef(
    new Set<OnboardingCommunityDecision>(),
  );
  const appliedCommunitySideEffectsRef = useRef(
    new Set<OnboardingCommunityDecision>(),
  );

  const trackOnboardingEvent = useCallback(
    (
      event: string,
      properties: Record<string, string | number | boolean | undefined> = {},
    ) => {
      if (previewMode) return;
      safeTrackEvent(event, properties);
    },
    [previewMode],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
    };
  }, []);

  useLayoutEffect(() => {
    if (open) stepHeadingRef.current?.focus({ preventScroll: true });
  }, [open, step]);

  useEffect(() => {
    if (open) {
      if (!onboardingStartedRef.current) {
        onboardingStartedRef.current = true;
        trackOnboardingEvent('onboarding_started');
      }
      return;
    }

    requestGenerationRef.current += 1;
    persistedRef.current = false;
    setStep('objective');
    setObjectiveChoice(null);
    setExperience(null);
    setPendingCourseOfferDecision(null);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    onboardingStartedRef.current = false;
    courseOfferViewedRef.current = false;
    communityOfferViewedRef.current = false;
    brandsLandingOpenedRef.current = false;
    trackedOfferDecisionsRef.current.clear();
    trackedCommunityDecisionsRef.current.clear();
    appliedCommunitySideEffectsRef.current.clear();
  }, [open, trackOnboardingEvent]);

  const recordCourseOffer = (
    event: OnboardingCourseOfferDecision | 'viewed',
    selectedExperience: OnboardingExperience,
  ) => {
    if (previewMode) return;
    try {
      const pending = recordOnboardingCourseOffer({
        variables: { event, experience: selectedExperience },
      });
      void Promise.resolve(pending).catch((error) => {
        console.warn('Unable to record onboarding course offer', error);
      });
    } catch (error) {
      console.warn('Unable to record onboarding course offer', error);
    }
  };

  const showCourseOffer = (
    choice: OnboardingObjectiveChoice,
    selectedExperience: OnboardingExperience,
  ) => {
    setStep('courseOffer');
    recordCourseOffer('viewed', selectedExperience);
    if (!courseOfferViewedRef.current) {
      courseOfferViewedRef.current = true;
      trackOnboardingEvent(
        'onboarding_course_offer_viewed',
        buildSelectionAnalyticsProperties(choice),
      );
    }
  };

  const showCommunityOffer = (
    choice: OnboardingObjectiveChoice,
    selectedExperience: OnboardingExperience,
  ) => {
    setStep('communityOffer');
    if (!communityOfferViewedRef.current) {
      communityOfferViewedRef.current = true;
      trackOnboardingEvent('onboarding_community_offer_shown', {
        ...buildSelectionAnalyticsProperties(choice),
        experience: selectedExperience,
      });
    }
  };

  const persistSelection = async (
    choice: OnboardingObjectiveChoice,
    selectedExperience: OnboardingExperience | null,
    destination: string,
    courseOfferDecision: OnboardingCourseOfferDecision | null,
  ): Promise<boolean> => {
    if (isSubmittingRef.current || persistedRef.current) return false;

    if (previewMode) {
      persistedRef.current = true;
      try {
        await onComplete();
      } catch (error) {
        console.warn('Unable to complete onboarding preview', error);
      }
      return true;
    }

    const selection = resolveObjectiveSelection(choice);
    const requestGeneration = ++requestGenerationRef.current;
    const requestIsCurrent = () =>
      mountedRef.current &&
      requestGenerationRef.current === requestGeneration;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      await updateOnboarding({
        variables: {
          onboardingObjective: selection.onboardingObjective,
          jobTitle: selection.jobTitle,
          onboardingExperience: selectedExperience,
        },
      });
    } catch (error) {
      if (!requestIsCurrent()) return false;

      console.error('Failed to update onboarding:', error);
      toast({
        title: t('onboarding.toast.error'),
        description: t('onboarding.toast.couldNotUpdate'),
        variant: 'destructive',
      });
      return false;
    } finally {
      if (requestIsCurrent()) {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }

    if (!requestIsCurrent()) return false;

    persistedRef.current = true;
    let completionResult: boolean | void | Promise<unknown>;
    try {
      completionResult = onComplete();
    } catch (error) {
      console.warn(
        'Unable to refresh onboarding data after persistence',
        error,
      );
      completionResult = undefined;
    }

    if (completionResult === false) return false;

    trackOnboardingEvent(
      'onboarding_completed',
      buildOnboardingCompletedProperties({
        selection,
        experience: selectedExperience,
        courseOfferDecision,
      }),
    );

    if (courseOfferDecision === 'accepted') {
      try {
        await completionResult;
      } catch (error) {
        console.warn(
          'Unable to refresh onboarding data after persistence',
          error,
        );
      }

      try {
        await onRequestViralizeCourse(null);
      } catch (error) {
        console.warn('Unable to start Viralize qualification', error);
      }
      return true;
    }

    try {
      void Promise.resolve(completionResult).catch((error) => {
        console.warn(
          'Unable to refresh onboarding data after persistence',
          error,
        );
      });
    } catch (error) {
      console.warn('Unable to refresh onboarding data after persistence', error);
    }

    if (location.pathname !== destination) navigate(destination);
    return true;
  };

  const handleObjective = (choice: OnboardingObjectiveChoice) => {
    if (isSubmittingRef.current) return;

    setObjectiveChoice(choice);
    trackOnboardingEvent(
      'onboarding_objective_selected',
      buildSelectionAnalyticsProperties(choice),
    );

    const selection = resolveObjectiveSelection(choice);
    if (selection.asksExperience) {
      setStep('experience');
      return;
    }

    // Hiring clippers is sold on the site. Open it here, in the click: a tab
    // opened after the save's await is blocked as a popup. Once per session,
    // so retrying a failed save does not open a second tab.
    if (choice === 'hire_clippers' && !brandsLandingOpenedRef.current) {
      brandsLandingOpenedRef.current = true;
      try {
        window.open(
          buildBrandsLandingUrl('onboarding', profile),
          '_blank',
          'noopener,noreferrer',
        );
      } catch (error) {
        console.warn('Unable to open the brands page', error);
      }
    }

    void persistSelection(choice, null, '/projects', null);
  };

  const handleExperience = (value: OnboardingExperience) => {
    if (!objectiveChoice || isSubmittingRef.current) return;

    setPendingCourseOfferDecision(null);
    setExperience(value);
    const properties = {
      ...buildSelectionAnalyticsProperties(objectiveChoice),
      experience: value,
    };
    trackOnboardingEvent('onboarding_experience_selected', properties);

    if (resolveExperienceOffer(value) === 'course') {
      showCourseOffer(objectiveChoice, value);
      return;
    }

    showCommunityOffer(objectiveChoice, value);
  };

  const handleOfferDecision = (decision: OnboardingCourseOfferDecision) => {
    if (!objectiveChoice || !experience || isSubmittingRef.current) return;

    if (!trackedOfferDecisionsRef.current.has(decision)) {
      trackedOfferDecisionsRef.current.add(decision);
      trackOnboardingEvent(
        decision === 'accepted'
          ? 'onboarding_course_offer_clicked'
          : 'onboarding_course_offer_dismissed',
        {
          ...buildSelectionAnalyticsProperties(objectiveChoice),
          experience,
        },
      );
    }

    recordCourseOffer(decision, experience);

    if (decision === 'dismissed') {
      setPendingCourseOfferDecision(decision);
      showCommunityOffer(objectiveChoice, experience);
      return;
    }

    void persistSelection(
      objectiveChoice,
      experience,
      '/courses/viralize-em-4-semanas',
      decision,
    );
  };

  const handleCommunityDecision = (decision: OnboardingCommunityDecision) => {
    if (!objectiveChoice || !experience || isSubmittingRef.current) return;

    if (!trackedCommunityDecisionsRef.current.has(decision)) {
      trackedCommunityDecisionsRef.current.add(decision);
      trackOnboardingEvent(
        decision === 'joined'
          ? 'onboarding_community_offer_joined'
          : 'onboarding_community_offer_dismissed',
        {
          ...buildSelectionAnalyticsProperties(objectiveChoice),
          experience,
        },
      );
    }

    if (!appliedCommunitySideEffectsRef.current.has(decision)) {
      appliedCommunitySideEffectsRef.current.add(decision);

      if (!previewMode) {
        try {
          dismissCommunityPrompt(window.localStorage);
        } catch (error) {
          console.warn('Unable to dismiss community prompt', error);
        }
      }

      if (decision === 'joined') {
        try {
          window.open(
            WHATSAPP_COMMUNITY_URL,
            '_blank',
            'noopener,noreferrer',
          );
        } catch (error) {
          console.warn('Unable to open community invitation', error);
        }

        if (!previewMode) {
          try {
            const markCommunityJoinedPromise = markCommunityJoined();
            void markCommunityJoinedPromise.catch((error) => {
              console.warn('Unable to mark community as joined', error);
            });
          } catch (error) {
            console.warn('Unable to mark community as joined', error);
          }
        }
      }
    }

    void persistSelection(
      objectiveChoice,
      experience,
      '/community-library',
      pendingCourseOfferDecision,
    );
  };

  const objectiveCards: Record<
    OnboardingObjectiveChoice,
    { copyKey: string; illustration: ReactNode }
  > = {
    ai_clipping: {
      copyKey: 'aiClipping',
      illustration: <OwnContentIllustration />,
    },
    clipping_channels: {
      copyKey: 'clippingChannels',
      illustration: <ClippingChannelIllustration />,
    },
    hire_clippers: {
      copyKey: 'hireClippers',
      illustration: <ClientWorkIllustration />,
    },
  };

  const objectiveOptions = ONBOARDING_OBJECTIVE_CHOICES.map((value) => {
    const { copyKey, illustration } = objectiveCards[value];
    return {
      value,
      heading: t(`onboarding.objective.${copyKey}.heading`),
      title: t(`onboarding.objective.${copyKey}.title`),
      description: t(`onboarding.objective.${copyKey}.description`),
      illustration,
    };
  });

  if (!open) return null;

  return (
    <Dialog open={open} modal>
      <DialogContent
        className="onboarding-surface max-h-[92dvh] w-[calc(100%-1.5rem)] max-w-5xl gap-0 overflow-y-auto rounded-[28px] border border-border p-0 text-foreground shadow-none !shadow-[shadow:var(--onboarding-shadow)] motion-reduce:animate-none sm:rounded-[32px]"
        hideCloseButton
        onOpenAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="relative isolate overflow-hidden px-5 py-7 sm:px-9 sm:py-9 lg:px-12 lg:py-11">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-10 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />

          {step === 'objective' && (
            <section>
              <DialogTitle
                ref={stepHeadingRef}
                tabIndex={-1}
                className="mx-auto max-w-3xl text-center text-2xl font-semibold leading-tight tracking-[-0.025em] text-foreground focus:outline-none sm:text-3xl lg:text-[2rem]"
              >
                {t('onboarding.objective.question')}
              </DialogTitle>
              <DialogDescription className="mx-auto mt-3 max-w-xl text-center text-sm leading-relaxed text-muted-foreground sm:text-base">
                {t('onboarding.objective.subtitle')}
              </DialogDescription>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:mt-7 sm:gap-4 md:grid-cols-3">
                {objectiveOptions.map((option) => (
                  <OnboardingOptionCard
                    key={option.value}
                    heading={option.heading}
                    title={option.title}
                    description={option.description}
                    illustration={option.illustration}
                    selected={objectiveChoice === option.value}
                    disabled={isSubmitting}
                    pending={isSubmitting && objectiveChoice === option.value}
                    pendingLabel={t('onboarding.saving')}
                    onClick={() => handleObjective(option.value)}
                  />
                ))}
              </div>
            </section>
          )}

          {step === 'experience' && (
            <section>
              <DialogTitle
                ref={stepHeadingRef}
                tabIndex={-1}
                className="mx-auto max-w-3xl text-center text-2xl font-semibold leading-tight tracking-[-0.025em] text-foreground focus:outline-none sm:text-3xl lg:text-[2rem]"
              >
                {t('onboarding.experience.question')}
              </DialogTitle>
              <DialogDescription className="mx-auto mt-3 max-w-xl text-center text-sm leading-relaxed text-muted-foreground sm:text-base">
                {t('onboarding.experience.subtitle')}
              </DialogDescription>

              <div className="mx-auto mt-5 grid max-w-3xl grid-cols-2 gap-3 sm:mt-7 sm:gap-4">
                {ONBOARDING_EXPERIENCES.map((value, index) => (
                  <OnboardingOptionCard
                    key={value}
                    title={t(`onboarding.experience.options.${value}`)}
                    eyebrow={String(index + 1).padStart(2, '0')}
                    selected={experience === value}
                    disabled={isSubmitting}
                    onClick={() => handleExperience(value)}
                    compact
                  />
                ))}
              </div>
            </section>
          )}

          {step === 'courseOffer' && (
            <OnboardingCourseOffer
              headingRef={stepHeadingRef}
              title={t('onboarding.courseOffer.title')}
              eyebrow={t('onboarding.courseOffer.eyebrow')}
              description={t('onboarding.courseOffer.description')}
              detail={t('onboarding.courseOffer.detail')}
              visualLabel={t('onboarding.courseOffer.visualLabel')}
              playerCopy={{
                playLabel: t('onboarding.courseOffer.player.play'),
                pauseLabel: t('onboarding.courseOffer.player.pause'),
                seekLabel: t('onboarding.courseOffer.player.seek'),
                muteLabel: t('onboarding.courseOffer.player.mute'),
                unmuteLabel: t('onboarding.courseOffer.player.unmute'),
                volumeLabel: t('onboarding.courseOffer.player.volume'),
                fullscreenLabel: t(
                  'onboarding.courseOffer.player.fullscreen',
                ),
                enableAudioLabel: t(
                  'onboarding.courseOffer.player.enableAudio',
                ),
                errorTitle: t('onboarding.courseOffer.player.errorTitle'),
                retryLabel: t('onboarding.courseOffer.player.retry'),
              }}
              primaryLabel={t('onboarding.courseOffer.primary')}
              secondaryLabel={t('onboarding.courseOffer.secondary')}
              pendingLabel={t('onboarding.saving')}
              pending={isSubmitting}
              onPrimary={() => handleOfferDecision('accepted')}
              onSecondary={() => handleOfferDecision('dismissed')}
            />
          )}

          {step === 'communityOffer' && (
            <OnboardingCommunityOffer
              headingRef={stepHeadingRef}
              title={t('onboarding.communityOffer.title')}
              description={t('onboarding.communityOffer.description')}
              primaryLabel={t('onboarding.communityOffer.primary')}
              secondaryLabel={t('onboarding.communityOffer.secondary')}
              pendingLabel={t('onboarding.saving')}
              pending={isSubmitting}
              onPrimary={() => handleCommunityDecision('joined')}
              onSecondary={() => handleCommunityDecision('dismissed')}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
